import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { formatDate } from "@/lib/date";
import { useIntegrations, type ConnectedProvider } from "@/hooks/useIntegrations";
import { useIdentityConnections } from "@/hooks/useIdentityConnections";
import { useOrganization, type OrgResponse } from "@/hooks/useOrganization";
import { useOrgProducts } from "@/hooks/useOrgProducts";
import { useAuth } from "@/auth/AuthProvider";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { api, ApiError } from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/ui/page-header";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { EmptyState } from "@/components/ui/empty-state";
import { MemberAvatar } from "@/components/ui/avatar-initials";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AlertTriangle, CalendarDays, ChevronDown, ExternalLink, GitBranch, Github, Loader2, Plug, Settings, ShieldCheck, X } from "lucide-react";
import { cn } from "@/lib/utils";

type IdentityProvider = "atlassian" | "google";

interface AtlassianSite {
  cloud_id: string;
  url: string;
  name: string;
}

function ProviderIcon({ provider, className }: { provider: string; className?: string }) {
  if (provider === "github") return <Github className={cn("h-4 w-4", className)} />;
  return <GitBranch className={cn("h-4 w-4", className)} />;
}

function ServiceRow({
  icon: Icon,
  name,
  description,
  connected,
}: {
  icon: React.ElementType;
  name: string;
  description: string;
  connected: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between rounded-lg border bg-card px-4 py-3 transition-colors hover:bg-muted/40">
      <div className="flex items-center gap-3">
        <div className={cn(
          "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ring-1",
          connected
            ? "bg-primary/10 ring-primary/20 text-primary"
            : "bg-muted ring-border text-muted-foreground",
        )}>
          <Icon className="h-4 w-4" />
        </div>
        <div>
          <p className="text-sm font-medium">{name}</p>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
      </div>
      {connected && (
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
          <span className="h-2 w-2 rounded-full bg-emerald-500" />
          {t("integrations.status.active")}
        </span>
      )}
    </div>
  );
}

function AtlassianSitePickerDialog({
  open,
  onOpenChange,
  onComplete,
  initialSites,
  pendingToken,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: () => void;
  initialSites?: AtlassianSite[];
  pendingToken?: string | null;
}) {
  const { t } = useTranslation();
  const [sites, setSites] = useState<AtlassianSite[]>([]);
  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [noToken, setNoToken] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedCloudId, setSelectedCloudId] = useState<string | null>(null);

  const isRedirectMode = (initialSites?.length ?? 0) > 0 && !!pendingToken;

  useEffect(() => {
    if (!open) return;
    setErrorMessage(null);
    setNoToken(false);
    setSelectedCloudId(null);

    if (isRedirectMode) {
      setSites(initialSites!);
      setLoading(false);
      return;
    }

    setLoading(true);
    api<AtlassianSite[]>("/api/v2/identity/oauth/atlassian/sites")
      .then((data) => {
        setSites(data);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) {
          setNoToken(true);
        } else {
          setErrorMessage(t("integrations.orgAtlassian.sitePicker.error"));
        }
      })
      .finally(() => setLoading(false));
  }, [open, t, isRedirectMode, initialSites]);

  const handleConfirm = useCallback(async () => {
    if (!selectedCloudId) return;
    setConnecting(true);
    setErrorMessage(null);
    try {
      if (isRedirectMode) {
        await api("/api/v2/identity/oauth/atlassian/select-site", {
          method: "POST",
          body: JSON.stringify({ pending_token: pendingToken, cloud_id: selectedCloudId }),
        });
      } else {
        await api("/api/v2/identity/oauth/atlassian/bind-site", {
          method: "POST",
          body: JSON.stringify({ cloud_id: selectedCloudId }),
        });
      }
      onComplete();
    } catch {
      setErrorMessage(t("integrations.orgAtlassian.sitePicker.error"));
    } finally {
      setConnecting(false);
    }
  }, [selectedCloudId, onComplete, t, isRedirectMode, pendingToken]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("integrations.orgAtlassian.sitePicker.title")}</DialogTitle>
          <DialogDescription>
            {t("integrations.orgAtlassian.sitePicker.description")}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t("integrations.orgAtlassian.sitePicker.loading")}
          </div>
        ) : noToken ? (
          <p className="py-4 text-sm text-muted-foreground">
            {t("integrations.orgAtlassian.sitePicker.noToken")}
          </p>
        ) : (
          <div className="space-y-2 py-2">
            {sites.map((site) => (
              <button
                key={site.cloud_id}
                type="button"
                onClick={() => setSelectedCloudId(site.cloud_id)}
                className={cn(
                  "flex w-full items-center justify-between rounded-lg border px-4 py-3 text-left transition-colors hover:bg-muted/40",
                  selectedCloudId === site.cloud_id ? "border-primary ring-1 ring-primary" : "border-border",
                )}
              >
                <div>
                  <p className="text-sm font-medium">{site.name}</p>
                  <p className="text-xs text-muted-foreground">{site.url}</p>
                </div>
                <div
                  className={cn(
                    "h-4 w-4 shrink-0 rounded-full border",
                    selectedCloudId === site.cloud_id ? "border-primary bg-primary" : "border-muted-foreground",
                  )}
                />
              </button>
            ))}
          </div>
        )}

        {errorMessage && <p className="text-sm text-destructive">{errorMessage}</p>}

        {!noToken && !loading && (
          <DialogFooter>
            <Button onClick={handleConfirm} disabled={!selectedCloudId || connecting}>
              {connecting
                ? t("integrations.orgAtlassian.sitePicker.connecting")
                : t("integrations.orgAtlassian.sitePicker.confirm")}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ServiceConnectionsSection({ providers }: { providers: ConnectedProvider[] }) {
  const { t } = useTranslation();
  const orgGithub = providers.find((p) => p.provider === "github" && !p.member_id);
  const orgGitlab = providers.find((p) => p.provider === "gitlab" && !p.member_id);
  const connectedCount = [orgGithub, orgGitlab].filter(Boolean).length;

  return (
    <div className="rounded-lg border bg-card" data-testid="service-connections-section">
      <div className="border-b px-5 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold">{t("integrations.serviceConnections.title")}</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {t("integrations.serviceConnections.description")}
            </p>
          </div>
          {connectedCount > 0 && (
            <div className="flex items-center gap-1.5 rounded-full bg-background px-3 py-1 text-xs font-medium ring-1 ring-border">
              <ShieldCheck className="h-3 w-3 text-primary" />
              <span className="font-bold text-foreground">{connectedCount}</span>
              <span className="text-muted-foreground">{t("integrations.serviceConnections.connected")}</span>
            </div>
          )}
        </div>
      </div>
      <div className="space-y-2 p-4">
        <ServiceRow
          icon={Github}
          name={t("integrations.serviceConnections.github")}
          description={orgGithub ? t("integrations.serviceConnections.discoveryDescription") : t("integrations.serviceConnections.notConfigured")}
          connected={!!orgGithub}
        />
        <ServiceRow
          icon={GitBranch}
          name={t("integrations.serviceConnections.gitlab")}
          description={orgGitlab ? t("integrations.serviceConnections.discoveryDescription") : t("integrations.serviceConnections.notConfigured")}
          connected={!!orgGitlab}
        />
      </div>
    </div>
  );
}

function TeamConnectionsSection({ providers }: { providers: ConnectedProvider[] }) {
  const { t } = useTranslation();
  const memberConnections = providers.filter((p) => p.member_id);

  return (
    <div className="rounded-lg border bg-card">
      <div className="border-b px-5 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold">{t("integrations.teamConnections.title")}</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {t("integrations.teamConnections.description")}
            </p>
          </div>
          {memberConnections.length > 0 && (
            <div className="flex items-center gap-1.5 rounded-full bg-background px-3 py-1 text-xs font-medium ring-1 ring-border">
              <span className="font-bold text-foreground">{memberConnections.length}</span>
              <span className="text-muted-foreground">
                {t("integrations.teamConnections.memberCount", { count: memberConnections.length })}
              </span>
            </div>
          )}
        </div>
      </div>

      {memberConnections.length === 0 ? (
        <div className="p-4">
          <EmptyState
            icon={GitBranch}
            title={t("integrations.teamConnections.empty.title")}
            description={t("integrations.teamConnections.empty.description")}
          />
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>
                <span className="flex items-center gap-1.5">{t("integrations.teamConnections.table.member")}</span>
              </TableHead>
              <TableHead>
                <span className="flex items-center gap-1.5">{t("integrations.teamConnections.table.provider")}</span>
              </TableHead>
              <TableHead>
                <span className="flex items-center gap-1.5">{t("integrations.teamConnections.table.username")}</span>
              </TableHead>
              <TableHead>
                <span className="flex items-center gap-1.5">
                  <CalendarDays className="h-3.5 w-3.5" />{t("integrations.teamConnections.table.connected")}
                </span>
              </TableHead>
              <TableHead>{t("integrations.teamConnections.table.status")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {memberConnections.map((c) => (
              <TableRow key={`${c.provider}-${c.member_id}`}>
                <TableCell>
                  <div className="flex items-center gap-2.5">
                    <MemberAvatar name={c.member_name ?? c.member_email ?? "?"} />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">
                        {c.member_name ?? "—"}
                      </p>
                      {c.member_email && (
                        <p className="truncate text-xs text-muted-foreground">{c.member_email}</p>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5 text-sm">
                    <ProviderIcon provider={c.provider} className="text-muted-foreground" />
                    <span className="capitalize">{c.provider}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <a
                    href={
                      c.provider === "github"
                        ? `https://github.com/${c.username}`
                        : `https://gitlab.com/${c.username}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-sm text-primary hover:underline"
                  >
                    @{c.username}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {formatDate(c.connected_at)}
                </TableCell>
                <TableCell>
                  {c.token_valid === false ? (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-red-600 dark:text-red-400">
                      <span className="h-2 w-2 rounded-full bg-red-500" />
                      {t("integrations.status.expired")}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                      <span className="h-2 w-2 rounded-full bg-emerald-500" />
                      {t("integrations.status.active")}
                    </span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

function OrgAtlassianSection({
  org,
  isAdmin,
  onConnect,
  hasAtlassianToken,
  onConfigureSite,
}: {
  org: OrgResponse | null;
  isAdmin: boolean;
  onConnect: () => void;
  hasAtlassianToken: boolean;
  onConfigureSite: () => void;
}) {
  const { t } = useTranslation();
  const connected = !!org?.jira_site_url;

  return (
    <div className="space-y-3">
      <div>
        <h4 className="text-sm font-semibold">{t("integrations.orgAtlassian.name")}</h4>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {t("integrations.orgAtlassian.description")}
        </p>
      </div>
      <div className="flex items-center justify-between rounded-lg border bg-card px-4 py-3 transition-colors hover:bg-muted/40">
        <div className="flex items-center gap-3">
          <div className={cn(
            "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ring-1",
            connected
              ? "bg-primary/10 ring-primary/20 text-primary"
              : "bg-muted ring-border text-muted-foreground",
          )}>
            <GitBranch className="h-4 w-4" />
          </div>
          <div>
            <p className="text-sm font-medium">
              {connected
                ? t("integrations.orgAtlassian.connectedDescription", { site: org?.jira_site_url })
                : t("integrations.orgAtlassian.notConfigured")}
            </p>
          </div>
        </div>
        {connected ? (
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
            <span className="h-2 w-2 rounded-full bg-emerald-500" />
            {t("integrations.status.active")}
          </span>
        ) : hasAtlassianToken && isAdmin ? (
          <Button variant="outline" size="sm" onClick={onConfigureSite}>
            <Settings className="h-3.5 w-3.5" />
            {t("integrations.orgAtlassian.configureSiteButton", "Configure site")}
          </Button>
        ) : isAdmin ? (
          <Button variant="outline" size="sm" onClick={onConnect}>
            {t("integrations.orgAtlassian.connectButton")}
          </Button>
        ) : (
          <span className="text-xs text-muted-foreground">
            {t("integrations.orgAtlassian.adminOnlyNote")}
          </span>
        )}
      </div>
    </div>
  );
}


const GCP_PROJECT_ID_PATTERN = /^[a-z][a-z0-9-]{4,28}[a-z0-9]$/;

function InferenceProviderSection({
  org,
  patchOrg,
}: {
  org: OrgResponse;
  patchOrg: (fields: Partial<OrgResponse>) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [gcpProjectId, setGcpProjectId] = useState(org.gcp_project_id ?? "");
  const [saving, setSaving] = useState(false);

  const gcpError = gcpProjectId !== "" && !GCP_PROJECT_ID_PATTERN.test(gcpProjectId);

  async function handleSave() {
    setSaving(true);
    try {
      await patchOrg({ gcp_project_id: gcpProjectId });
      toast.success(t("integrations.pmo.inference.saveSuccess"));
    } catch {
      toast.error(t("integrations.pmo.inference.saveError"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="space-y-3">
      <div>
        <h4 className="text-sm font-semibold">{t("integrations.pmo.inference.title")}</h4>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {t("integrations.pmo.inference.description")}
        </p>
      </div>
      {!org.gcp_project_id && (
        <div className="flex items-center gap-2 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {t("integrations.pmo.inference.missingWarning")}
        </div>
      )}
      <p className="text-sm text-muted-foreground">
        {t("integrations.pmo.inference.guide")}
      </p>
      <div className="space-y-2">
        <Label htmlFor="gcpProjectId">{t("integrations.pmo.inference.gcpProjectIdLabel")}</Label>
        <Input
          id="gcpProjectId"
          type="text"
          placeholder={t("integrations.pmo.inference.gcpProjectIdPlaceholder")}
          value={gcpProjectId}
          onChange={(e) => setGcpProjectId(e.target.value)}
          disabled={saving}
        />
        {gcpError && (
          <p className="text-sm text-destructive">
            {t("integrations.pmo.inference.gcpProjectIdFormatError")}
          </p>
        )}
      </div>
      <Button type="submit" disabled={saving || gcpError} size="sm">
        {saving ? (
          <>
            <Loader2 className="animate-spin" />
            {t("integrations.pmo.inference.saving")}
          </>
        ) : (
          t("integrations.pmo.inference.save")
        )}
      </Button>
    </form>
  );
}

function ProductRow({
  name,
  description,
  active,
  isAdmin,
  activating,
  onActivate,
  onConfigure,
  expanded,
}: {
  name: string;
  description: string;
  active: boolean;
  isAdmin: boolean;
  activating: boolean;
  onActivate: () => void;
  onConfigure: () => void;
  expanded: boolean;
}) {
  const { t } = useTranslation();

  return (
    <div className="flex items-center justify-between rounded-lg border bg-card px-4 py-3 transition-colors hover:bg-muted/40">
      <div className="flex items-center gap-3">
        <div className={cn(
          "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ring-1",
          active
            ? "bg-primary/10 ring-primary/20 text-primary"
            : "bg-muted ring-border text-muted-foreground",
        )}>
          <Plug className="h-4 w-4" />
        </div>
        <div>
          <p className="text-sm font-medium">{name}</p>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
      </div>
      <div data-testid="pmo-status-badge">
        {active ? (
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
              <span className="h-2 w-2 rounded-full bg-emerald-500" />
              {t("integrations.pmo.statusActive")}
            </span>
            {isAdmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={onConfigure}
                data-testid="pmo-configure-button"
              >
                <Settings className="h-3.5 w-3.5" />
                {t("integrations.pmo.configure")}
                <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", expanded && "rotate-180")} />
              </Button>
            )}
          </div>
        ) : isAdmin ? (
          <Button
            variant="outline"
            size="sm"
            onClick={onActivate}
            disabled={activating}
            data-testid="pmo-activate-button"
          >
            {activating && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {t("integrations.pmo.activate")}
          </Button>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
            <span className="h-2 w-2 rounded-full bg-muted-foreground/50" />
            {t("integrations.pmo.statusInactive")}
          </span>
        )}
      </div>
    </div>
  );
}

function RaiseIntegrationsSection({
  org,
  orgLoading,
  orgError,
  patchOrg,
  isAdmin,
  isPmoActive,
  activatingPmo,
  onActivate,
  onAtlassianConnect,
  hasAtlassianToken,
  onAtlassianConfigureSite,
}: {
  org: OrgResponse | null;
  orgLoading: boolean;
  orgError: string | null;
  patchOrg: (fields: Partial<OrgResponse>) => Promise<void>;
  isAdmin: boolean;
  isPmoActive: boolean;
  activatingPmo: boolean;
  onActivate: () => void;
  onAtlassianConnect: () => void;
  hasAtlassianToken: boolean;
  onAtlassianConfigureSite: () => void;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const activeCount = isPmoActive ? 1 : 0;

  return (
    <div className="rounded-lg border bg-card">
      <div className="border-b px-5 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold">{t("integrations.raise.title")}</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {t("integrations.raise.description")}
            </p>
          </div>
          {activeCount > 0 && (
            <div className="flex items-center gap-1.5 rounded-full bg-background px-3 py-1 text-xs font-medium ring-1 ring-border">
              <ShieldCheck className="h-3 w-3 text-primary" />
              <span className="font-bold text-foreground">{activeCount}</span>
              <span className="text-muted-foreground">{t("integrations.raise.active")}</span>
            </div>
          )}
        </div>
      </div>
      <div className="space-y-2 p-4">
        <ProductRow
          name={t("integrations.pmo.title")}
          description={t("integrations.pmo.description")}
          active={isPmoActive}
          isAdmin={isAdmin}
          activating={activatingPmo}
          onActivate={onActivate}
          onConfigure={() => setExpanded(!expanded)}
          expanded={expanded}
        />
        {expanded && isPmoActive && (
          <div className="space-y-6 rounded-lg border bg-muted/30 p-4">
            {orgLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            ) : orgError ? (
              <div className="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                {orgError}
              </div>
            ) : org ? (
              <>
                <InferenceProviderSection org={org} patchOrg={patchOrg} />
                <OrgAtlassianSection org={org} isAdmin={isAdmin} onConnect={onAtlassianConnect} hasAtlassianToken={hasAtlassianToken} onConfigureSite={onAtlassianConfigureSite} />
              </>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

const DOMAIN_PATTERN = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i;

function isValidDomain(value: string): boolean {
  return DOMAIN_PATTERN.test(value.trim());
}

function isDuplicateDomain(value: string, existing: string[]): boolean {
  const normalized = value.trim().toLowerCase();
  return existing.some((d) => d.toLowerCase() === normalized);
}

function OrgDomainsSection({
  org,
  patchOrg,
}: {
  org: OrgResponse;
  patchOrg: (fields: Partial<OrgResponse>) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [domains, setDomains] = useState<string[]>(org.allowed_email_domains);
  const [newDomain, setNewDomain] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  function handleAdd() {
    const value = newDomain.trim();
    if (!value) return;
    if (!isValidDomain(value)) {
      setError(t("integrations.orgDomains.invalidFormatError"));
      return;
    }
    if (isDuplicateDomain(value, domains)) {
      setError(t("integrations.orgDomains.duplicateError"));
      return;
    }
    setDomains([...domains, value.toLowerCase()]);
    setNewDomain("");
    setError(null);
  }

  function handleRemove(domain: string) {
    setDomains(domains.filter((d) => d !== domain));
  }

  async function handleSave() {
    setSaving(true);
    try {
      await patchOrg({ allowed_email_domains: domains });
      toast.success(t("integrations.orgDomains.saveSuccess"));
    } catch {
      toast.error(t("integrations.orgDomains.saveError"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rounded-lg border bg-card">
      <div className="border-b px-5 py-4">
        <h3 className="text-sm font-semibold">{t("integrations.orgDomains.title")}</h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {t("integrations.orgDomains.description")}
        </p>
      </div>
      <div className="space-y-3 p-4">
        <div className="flex flex-wrap gap-2">
          {domains.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t("integrations.orgDomains.emptyState")}
            </p>
          ) : (
            domains.map((domain) => (
              <Badge key={domain} variant="secondary" className="gap-1.5 py-1 pr-1.5">
                {domain}
                <button
                  type="button"
                  onClick={() => handleRemove(domain)}
                  aria-label={t("integrations.orgDomains.removeLabel", { domain })}
                  className="rounded-full p-0.5 hover:bg-muted-foreground/20"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))
          )}
        </div>
        <div className="flex gap-2">
          <div className="flex-1">
            <Label htmlFor="newDomain" className="sr-only">
              {t("integrations.orgDomains.addLabel")}
            </Label>
            <Input
              id="newDomain"
              type="text"
              placeholder={t("integrations.orgDomains.addPlaceholder")}
              value={newDomain}
              onChange={(e) => {
                setNewDomain(e.target.value);
                setError(null);
              }}
              disabled={saving}
            />
          </div>
          <Button type="button" variant="outline" size="sm" onClick={handleAdd} disabled={saving}>
            {t("integrations.orgDomains.addButton")}
          </Button>
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <Button type="button" size="sm" onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <Loader2 className="animate-spin" />
              {t("integrations.orgDomains.saving")}
            </>
          ) : (
            t("integrations.orgDomains.saveButton")
          )}
        </Button>
      </div>
    </div>
  );
}

export function IntegrationsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = useIsAdmin();
  const { providers, loading, error } = useIntegrations();
  const { connections, refetch: refetchConnections } = useIdentityConnections();
  const { org, loading: orgLoading, error: orgError, patchOrg } = useOrganization();
  const { isProductActive, activateProduct } = useOrgProducts();
  const [activatingPmo, setActivatingPmo] = useState(false);
  const isPmoActive = isProductActive("raise-pmo");
  const [sitePickerOpen, setSitePickerOpen] = useState(false);
  const [pendingSites, setPendingSites] = useState<AtlassianSite[]>([]);
  const [pendingToken, setPendingToken] = useState<string | null>(null);

  const totalConnected = providers.length;
  const memberConnections = providers.filter((p) => p.member_id).length;


  const handleActivatePmo = async () => {
    setActivatingPmo(true);
    try {
      await activateProduct("raise-pmo");
      toast.success(t("integrations.pmo.activateSuccess"));
    } catch {
      toast.error(t("integrations.pmo.activateError"));
    } finally {
      setActivatingPmo(false);
    }
  };

  const handleConnect = useCallback(
    (provider: IdentityProvider) => {
      if (!user) return;
      const params = new URLSearchParams({
        user_key: user.email,
        redirect_uri: `${window.location.origin}/${user.org_slug}/integrations`,
        // RAISE-15374: org-level flow — site binding via admin endpoints.
        origin: "org",
        // RAISE-15401: org_id removed — derived from auth context (session cookie).
      });
      window.location.href = `/api/v2/identity/oauth/${provider}/start?${params.toString()}`;
    },
    [user],
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get("status");
    if (!status) return;

    if (status === "success") {
      toast.success(t("integrations.oauthStatus.success"));
      refetchConnections();
      window.history.replaceState({}, "", window.location.pathname);
    } else if (status === "site_selection_required") {
      const rawSites = params.get("sites");
      const rawToken = params.get("pending_token");
      window.history.replaceState({}, "", window.location.pathname);

      if (rawSites && rawToken) {
        try {
          const sites = JSON.parse(rawSites) as AtlassianSite[];
          if (sites.length === 1) {
            // RAISE-15098 decision #1: nothing to pick between — auto-confirm
            // through the same ephemeral confirm endpoint, no picker UI.
            api("/api/v2/identity/oauth/atlassian/select-site", {
              method: "POST",
              body: JSON.stringify({
                pending_token: rawToken,
                cloud_id: sites[0].cloud_id,
              }),
            })
              .then(() => {
                toast.success(t("integrations.oauthStatus.success"));
                refetchConnections();
              })
              .catch(() => {
                toast.error(t("integrations.orgAtlassian.sitePicker.error"));
              });
            return;
          }
          setPendingSites(sites);
          setPendingToken(rawToken);
        } catch {
          // Fall through — dialog will fetch from API
        }
      }
      toast.message(t("integrations.oauthStatus.siteSelectionRequired"));
      setSitePickerOpen(true);
    } else if (status === "error") {
      const reason = params.get("reason") ?? "unknown";
      toast.error(t("integrations.oauthStatus.error", { reason }));
      window.history.replaceState({}, "", window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, refetchConnections]);

  const handleSitePickerComplete = useCallback(() => {
    setSitePickerOpen(false);
    setPendingSites([]);
    setPendingToken(null);
    toast.success(t("integrations.oauthStatus.success"));
    refetchConnections();
  }, [t, refetchConnections]);

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Plug}
        title={t("integrations.header.title")}
        subtitle={t("integrations.header.subtitle")}
        stats={
          !loading && totalConnected > 0
            ? [
                { value: totalConnected, label: t("integrations.header.totalConnections") },
                ...(memberConnections > 0
                  ? [{ value: memberConnections, label: t("integrations.header.teamMembers") }]
                  : []),
              ]
            : undefined
        }
      />

      {error && <p className="text-sm text-destructive">{error}</p>}

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t("integrations.loading")}
        </div>
      ) : (
        <>
          <RaiseIntegrationsSection
            org={org}
            orgLoading={orgLoading}
            orgError={orgError}
            patchOrg={patchOrg}
            isAdmin={isAdmin}
            isPmoActive={isPmoActive}
            activatingPmo={activatingPmo}
            onActivate={handleActivatePmo}
            onAtlassianConnect={() => handleConnect("atlassian")}
            hasAtlassianToken={connections.some(c => c.provider === "atlassian" && c.connected)}
            onAtlassianConfigureSite={() => setSitePickerOpen(true)}
          />
          {org && <OrgDomainsSection org={org} patchOrg={patchOrg} />}
          <ServiceConnectionsSection providers={providers} />
          <TeamConnectionsSection providers={providers} />
        </>
      )}

      <AtlassianSitePickerDialog
        open={sitePickerOpen}
        onOpenChange={(open) => {
          setSitePickerOpen(open);
          if (!open) {
            setPendingSites([]);
            setPendingToken(null);
          }
        }}
        onComplete={handleSitePickerComplete}
        initialSites={pendingSites}
        pendingToken={pendingToken}
      />
    </div>
  );
}
